import { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import PharmeasyLayout from "@/components/PharmeasyLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Download, FileText, Loader2, RefreshCw, Calendar } from "lucide-react";

const PharmeasyReport = ({ user, onLogout }) => {
  // Use the configured API from App.js
  const REPORT_API = API;
  
  const [downloading, setDownloading] = useState(false);
  
  // Report filters
  const [dateFilterType, setDateFilterType] = useState("delivered_at");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const dateFilterOptions = [
    { value: "delivered_at", label: "Delivered At" },
    { value: "imported_at", label: "Imported At" },
    { value: "created_at", label: "Created At" },
  ];

  const downloadOrderReport = async () => {
    if (!startDate || !endDate) {
      toast.error("Please select both start and end dates");
      return;
    }
    
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      
      if (dateFilterType === "delivered_at") {
        params.append('delivered_start', startDate);
        params.append('delivered_end', endDate);
      } else if (dateFilterType === "imported_at") {
        params.append('imported_start', startDate);
        params.append('imported_end', endDate);
      } else if (dateFilterType === "created_at") {
        params.append('created_start', startDate);
        params.append('created_end', endDate);
      }
      
      const response = await axios.get(
        `${REPORT_API}/pharmeasy-billing/download/report?${params.toString()}`,
        { responseType: 'blob' }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `order_report_${dateFilterType}_${startDate}_to_${endDate}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Order report downloaded successfully");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to download report");
    } finally {
      setDownloading(false);
    }
  };

  const clearFilters = () => {
    setDateFilterType("delivered_at");
    setStartDate("");
    setEndDate("");
  };

  return (
    <PharmeasyLayout user={user} onLogout={onLogout} activeTab="report">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Reports</h1>
            <p className="text-slate-500 text-sm mt-1">Download order reports with custom date filters</p>
          </div>
        </div>

        {/* Order Report Card */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <FileText className="w-5 h-5 text-slate-600" />
              Order Report
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <p className="text-sm text-slate-600">
                Select a date type and range to download the order report. 
                The report includes all shipment details including products.
              </p>
              
              {/* Date Filter Selection */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                <div>
                  <Label className="text-sm font-medium text-slate-700">Date Filter Type</Label>
                  <Select value={dateFilterType} onValueChange={setDateFilterType}>
                    <SelectTrigger className="mt-1" data-testid="date-filter-type-select">
                      <SelectValue placeholder="Select date type" />
                    </SelectTrigger>
                    <SelectContent>
                      {dateFilterOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">Start Date</Label>
                  <Input 
                    type="date" 
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="mt-1"
                    data-testid="report-start-date"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">End Date</Label>
                  <Input 
                    type="date" 
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="mt-1"
                    data-testid="report-end-date"
                  />
                </div>
              </div>

              {/* Selected Filter Info */}
              {startDate && endDate && (
                <div className="flex items-center gap-2 text-sm text-slate-600 bg-blue-50 p-4 rounded-lg border border-blue-100">
                  <Calendar className="w-4 h-4 text-blue-600" />
                  <span>
                    Downloading orders where <strong>{dateFilterOptions.find(o => o.value === dateFilterType)?.label}</strong> is between <strong>{startDate}</strong> and <strong>{endDate}</strong>
                  </span>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-4 pt-4 border-t">
                <Button 
                  onClick={downloadOrderReport}
                  disabled={downloading || !startDate || !endDate}
                  className="gap-2"
                  size="lg"
                  data-testid="download-report-btn"
                >
                  {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  Download Report
                </Button>
                <Button 
                  variant="outline"
                  onClick={clearFilters}
                  className="gap-2"
                  size="lg"
                  data-testid="clear-filters-btn"
                >
                  <RefreshCw className="w-4 h-4" />
                  Clear Filters
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </PharmeasyLayout>
  );
};

export default PharmeasyReport;
